// Storage abstraction: Cloudflare R2 (S3-compatible) when credentials are available,
// with a localStorage fallback for dev preview / when R2 is not configured.
import { AwsClient } from "aws4fetch";

const LS_PREFIX = "tahsin_ls_v2_";

// ---------- Utilities ----------
function isBlob(data) {
  return data instanceof Blob || (typeof File !== "undefined" && data instanceof File);
}

// ---------- Public read URL ----------
// Public content URL — first checks admin-configured public URL in localStorage,
// then REACT_APP_R2_PUBLIC_URL env var. If neither is set, returns null → fallback to local.
export function getPublicUrl() {
  const fromConfig = localStorage.getItem("tahsin_public_url");
  if (fromConfig) return fromConfig.replace(/\/$/, "");
  const fromEnv = process.env.REACT_APP_R2_PUBLIC_URL;
  if (fromEnv) return fromEnv.replace(/\/$/, "");
  return null;
}

export async function fetchPublicJSON(key) {
  const base = getPublicUrl();
  if (!base) return null;
  try {
    const res = await fetch(`${base}/${key}?t=${Date.now()}`, {
      cache: "no-store",
    });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

export function publicAssetUrl(key) {
  const base = getPublicUrl();
  if (!base) return null;
  return `${base}/${key}`;
}

// ---------- R2 (write) client ----------
// Credentials shape:
// { accountId, accessKeyId, secretAccessKey, bucket, publicUrl, endpoint? }
export function makeR2Client(creds) {
  const endpoint =
    creds.endpoint ||
    `https://${creds.accountId}.r2.cloudflarestorage.com`;
  const client = new AwsClient({
    accessKeyId: creds.accessKeyId,
    secretAccessKey: creds.secretAccessKey,
    service: "s3",
    region: "auto",
  });

  const bucketUrl = `${endpoint}/${creds.bucket}`;

  async function putObject(key, body, contentType) {
    const url = `${bucketUrl}/${encodeURIComponent(key).replace(/%2F/g, "/")}`;
    const req = await client.sign(url, {
      method: "PUT",
      body,
      headers: { "Content-Type": contentType || "application/octet-stream" },
    });
    const res = await fetch(req);
    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(`R2 upload failed (${res.status}): ${text}`);
    }
    return true;
  }

  async function getObject(key) {
    const url = `${bucketUrl}/${encodeURIComponent(key).replace(/%2F/g, "/")}`;
    const req = await client.sign(url, { method: "GET" });
    const res = await fetch(req);
    if (res.status === 404) return null;
    if (!res.ok) {
      throw new Error(`R2 get failed (${res.status})`);
    }
    return res;
  }

  async function deleteObject(key) {
    const url = `${bucketUrl}/${encodeURIComponent(key).replace(/%2F/g, "/")}`;
    const req = await client.sign(url, { method: "DELETE" });
    const res = await fetch(req);
    if (!res.ok && res.status !== 404) {
      throw new Error(`R2 delete failed (${res.status})`);
    }
    return true;
  }

  return { putObject, getObject, deleteObject, publicUrl: creds.publicUrl, bucketUrl };
}

// ---------- High-level helpers ----------
// Upload JSON object to R2 (admin write) AND update public URL cache so next read gets new data.
export async function uploadJSON(r2, key, data) {
  const body = JSON.stringify(data, null, 2);
  const blob = new Blob([body], { type: "application/json" });
  await r2.putObject(key, blob, "application/json");
  // Also cache locally so UI updates immediately even before public CDN propagates.
  try {
    localStorage.setItem(LS_PREFIX + key, body);
  } catch {
    /* quota */
  }
  return true;
}

function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

export async function uploadImage(r2, file, keyPrefix = "images", resizeOpts) {
  let uploadBlob = file;
  let thumbBlob = null;
  let mimeType = file.type;
  let ext = (file.name || "img").split(".").pop().toLowerCase() || "jpg";

  // Resize if options provided
  if (resizeOpts) {
    const { resizeImage } = await import("./imageResize");
    const result = await resizeImage(file, resizeOpts);
    uploadBlob = result.blob;
    thumbBlob = result.thumbBlob;
    mimeType = result.mimeType;
    ext = mimeType === "image/webp" ? "webp" : "jpg";
  }

  const safeExt = /^(jpg|jpeg|png|webp|gif|avif)$/.test(ext) ? ext : "jpg";
  const timestamp = Date.now();
  const rand = Math.random().toString(36).slice(2, 8);
  const key = `${keyPrefix}/${timestamp}-${rand}.${safeExt}`;
  const thumbKey = `${keyPrefix}/thumb-${timestamp}-${rand}.${safeExt}`;

  if (r2) {
    await r2.putObject(key, uploadBlob, mimeType || `image/${safeExt}`);
    if (thumbBlob) {
      await r2.putObject(thumbKey, thumbBlob, mimeType || `image/${safeExt}`);
    }
    const base = r2.publicUrl.replace(/\/$/, "");
    return {
      url: `${base}/${key}`,
      thumbUrl: thumbBlob ? `${base}/${thumbKey}` : `${base}/${key}`,
    };
  }

  // Local mode: convert both to data URLs
  const url = await blobToDataUrl(uploadBlob);
  const thumbUrl = thumbBlob ? await blobToDataUrl(thumbBlob) : url;
  return { url, thumbUrl };
}

// ---------- LocalStorage fallback (used when no R2 is configured) ----------
export const localBackend = {
  loadJSON(key, fallback = null) {
    try {
      const raw = localStorage.getItem(LS_PREFIX + key);
      if (raw == null) return fallback;
      return JSON.parse(raw);
    } catch {
      return fallback;
    }
  },
  saveJSON(key, data) {
    localStorage.setItem(LS_PREFIX + key, JSON.stringify(data));
  },
  hasKey(key) {
    return localStorage.getItem(LS_PREFIX + key) != null;
  },
  remove(key) {
    localStorage.removeItem(LS_PREFIX + key);
  },
};

// Unified load: try public R2 URL → fallback to localStorage → fallback to default.
export async function loadJSON(key, defaultValue) {
  // 1. try public R2
  const remote = await fetchPublicJSON(key);
  if (remote != null) {
    localBackend.saveJSON(key, remote); // cache
    return remote;
  }
  // 2. try local
  if (localBackend.hasKey(key)) return localBackend.loadJSON(key, defaultValue);
  // 3. default
  return defaultValue;
}

// Save JSON: if R2 client provided, upload to R2 (+ local cache); else local only.
export async function saveJSON(r2, key, data) {
  if (r2) {
    await uploadJSON(r2, key, data);
  } else {
    localBackend.saveJSON(key, data);
  }
}

export { isBlob };
